import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';

function AdminLogin() {
  const navigate = useNavigate();
  const { loginAdmin } = useAuth();
  const { currentTenant, getCompanyName, getTenantId } = useConfig();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Get company name - use tenant name if on subdomain, otherwise config
  const companyName = currentTenant?.companyName || getCompanyName();
  const tenantId = getTenantId();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Pass tenantId to login for subdomain-based tenant verification
      await loginAdmin(formData.email, formData.password, tenantId);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-box">
        {/* Admin Badge */}
        <div className="admin-access-badge">
          🔐 ADMIN ACCESS ONLY
        </div>

        <h2 className="admin-login-title">
          {currentTenant ? `${companyName} Admin` : 'Admin Login'}
        </h2>

        <p className="admin-login-subtitle">
          {currentTenant
            ? `Sign in to manage ${companyName}`
            : 'Authorized personnel only. Unauthorized access is prohibited.'
          }
        </p>

        {error && (
          <div className="admin-login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label className="admin-form-label">
              Admin Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="admin-form-input"
              disabled={loading}
            />
          </div>

          <div className="admin-form-group-last">
            <label className="admin-form-label">
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="admin-form-input"
                disabled={loading}
                style={{ paddingRight: '44px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  color: 'var(--color-text-light)',
                  fontSize: '1.1rem'
                }}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="admin-login-submit"
          >
            {loading ? 'Logging in...' : 'Access Admin Portal'}
          </button>
        </form>

        <div className="admin-login-footer">
          <Link
            to="/admin/forgot-password"
            className="admin-forgot-link"
            style={{ display: 'block', textAlign: 'center', marginBottom: '15px', color: 'var(--color-brand-text)', textDecoration: 'none' }}
          >
            Forgot your password?
          </Link>
          <button
            onClick={() => navigate('/')}
            className="admin-back-button"
          >
            ← Back to Main Site
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;